// =============================================================================
// exports-client.ts — Client des exports CSV référentiel ADMIN (S10-D3), DOUBLABLE.
// =============================================================================
// EXPORT = γ SYNCHRONE (tranché GATE_0 S10) : chaque export est un TÉLÉCHARGEMENT
// DIRECT (GET → fichier CSV), PAS un job/async/polling. Côté front, l'action déclenche
// le téléchargement navigateur de l'URL de l'endpoint correspondant.
//
// Comme les autres clients UI, expose une INTERFACE (ExportsClient) consommée par les
// hooks/écrans, derrière un DI seam (double déterministe en test, impl navigateur réelle
// en prod). Le double n'effectue AUCUNE I/O réseau : il enregistre l'intention de
// téléchargement (URL demandée) pour des tests fidèles sans navigateur.
//
// Les 4 endpoints (contrat D3 figé, AdminSessionGuard) :
//   GET /admin/suppliers/export.csv  — référentiel fournisseurs
//   GET /admin/clients/export.csv    — référentiel clients
//   GET /admin/cgu/export.csv        — registre des signatures CGU
//   GET /admin/skills/export.csv     — référentiel de compétences

/** Identifiant d'un export référentiel (clé stable, indépendante de l'i18n). */
export type ExportKind = 'suppliers' | 'clients' | 'cgu' | 'skills';

/** URL (chemin relatif racine API) de l'endpoint de chaque export (contrat D3 figé). */
export const EXPORT_PATHS: Readonly<Record<ExportKind, string>> = {
  suppliers: '/admin/suppliers/export.csv',
  clients: '/admin/clients/export.csv',
  cgu: '/admin/cgu/export.csv',
  skills: '/admin/skills/export.csv',
};

/**
 * URL de l'export CSV des DESTINATAIRES d'une campagne (γ SYNCHRONE, §2.7.3 / A-16).
 * Contrat back FIGÉ (CampaignRecipientsExportController, AdminSessionGuard) :
 *   GET /admin/campaigns/:id/recipients/export.csv
 * Paramétré par campaignId — à l'inverse des 4 exports référentiels globaux ci-dessus.
 */
export function campaignRecipientsExportPath(campaignId: string): string {
  return `/admin/campaigns/${encodeURIComponent(campaignId)}/recipients/export.csv`;
}

/**
 * Filtre de l'export du JOURNAL des signatures CGU (§2.9, DBU l.2116 « export des signatures
 * FILTRÉES »). Tous les champs OPTIONNELS : un champ absent/vide = pas de restriction sur cet axe.
 * PROPAGÉ tel quel aux query params de `/admin/cgu/export.csv` (l'API filtre, autorité serveur).
 * Le sous-filtre « raison sociale » (résolu FRONT via fan-out supplier-reference) n'a pas de query
 * param serveur : seul le SIRET est un axe serveur (invariant n°2, le BC legal ne résout pas l'identité).
 */
export interface CguSignaturesExportFilter {
  /** Version CGU (cguVersionId opaque). */
  readonly version?: string;
  /** SIRET signataire (14 chiffres). */
  readonly siret?: string;
  /** Borne basse de période (date civile `YYYY-MM-DD`). */
  readonly from?: string;
  /** Borne haute de période (date civile `YYYY-MM-DD`). */
  readonly to?: string;
}

/**
 * URL de l'export CSV du registre des signatures CGU FILTRÉ (§2.9). Réutilise l'endpoint figé
 * `/admin/cgu/export.csv` en y PROPAGEANT le filtre courant comme query string (URLSearchParams :
 * n'ajoute QUE les axes définis et non vides — l'export sans filtre reste l'URL nue). L'API produit
 * le CSV des seules lignes filtrées (DoD R3) et audite le rowCount post-filtre.
 */
export function cguSignaturesExportPath(filter: CguSignaturesExportFilter): string {
  const params = new URLSearchParams();
  const append = (key: string, value: string | undefined): void => {
    const trimmed = value?.trim() ?? '';
    if (trimmed.length > 0) {
      params.append(key, trimmed);
    }
  };
  append('version', filter.version);
  append('siret', filter.siret);
  append('from', filter.from);
  append('to', filter.to);
  const query = params.toString();
  return query.length > 0
    ? `${EXPORT_PATHS.cgu}?${query}`
    : EXPORT_PATHS.cgu;
}

/**
 * Filtre de l'EXPORT AVANCÉ COMPLIANCE du journal d'audit (§1.4.4, R15-GOV-06). Période OBLIGATOIRE côté
 * usage (le back exige `from`/`to`), types OPTIONNELS (0..n ; vide = tous les types). PROPAGÉ tel quel aux
 * query params de `/admin/audit/export.json` (l'API filtre, autorité serveur ; doctrine NON-NOMINATIF :
 * aucun tenant_id/ip/payload dans le document produit).
 */
export interface ComplianceAuditExportFilter {
  /** Borne basse de période (date civile `YYYY-MM-DD`). */
  readonly from?: string;
  /** Borne haute de période (date civile `YYYY-MM-DD`, incluse). */
  readonly to?: string;
  /** Types d'événement à inclure (répétés en `type=` ; absent/vide = tous les types). */
  readonly types?: readonly string[];
}

/**
 * URL de l'export JSON de compliance du journal d'audit FILTRÉ (§1.4.4). Endpoint admin analytics
 * (AuditComplianceAdminController, AdminSessionGuard) : `/admin/audit/export.json`. Le filtre courant
 * (période + types) est PROPAGÉ en query string (URLSearchParams : n'ajoute QUE les axes définis et non
 * vides ; `type` est répété pour chaque type). L'API produit le document JSON structuré des seules entrées
 * de la période (DoD R3). γ synchrone (téléchargement direct, aucun job/polling).
 */
export function complianceAuditExportPath(
  filter: ComplianceAuditExportFilter,
): string {
  const params = new URLSearchParams();
  const appendScalar = (key: string, value: string | undefined): void => {
    const trimmed = value?.trim() ?? '';
    if (trimmed.length > 0) {
      params.append(key, trimmed);
    }
  };
  appendScalar('from', filter.from);
  appendScalar('to', filter.to);
  for (const type of filter.types ?? []) {
    const trimmed = type.trim();
    if (trimmed.length > 0) {
      params.append('type', trimmed);
    }
  }
  const query = params.toString();
  return query.length > 0
    ? `/admin/audit/export.json?${query}`
    : '/admin/audit/export.json';
}

/**
 * Contrat du client d'exports référentiel ADMIN. Une SEULE responsabilité :
 * déclencher le téléchargement direct (GET → CSV) de l'export demandé. Aucune
 * règle métier ; l'autorité (RBAC AdminSessionGuard, K-anonymity, génération CSV)
 * est l'API. Synchrone du point de vue UX : pas de job, pas de polling.
 */
export interface ExportsClient {
  /** Déclenche le téléchargement direct du CSV de l'export référentiel `kind`. */
  download(kind: ExportKind): void;
  /**
   * Déclenche le téléchargement direct du CSV des destinataires de la campagne
   * `campaignId` (§2.7.3 / A-16). Back existant, pas de job/polling (γ synchrone).
   */
  downloadCampaignRecipients(campaignId: string): void;
  /**
   * Déclenche le téléchargement du CSV du registre des signatures CGU FILTRÉ (§2.9). Le `filter`
   * courant (version/siret/période) est PROPAGÉ en query params — l'export ne sort QUE les lignes
   * filtrées (DoD R3). Réutilise l'endpoint figé `/admin/cgu/export.csv` (γ synchrone).
   */
  downloadCguSignatures(filter: CguSignaturesExportFilter): void;
  /**
   * Déclenche le téléchargement du JSON de compliance du journal d'audit FILTRÉ (§1.4.4, R15-GOV-06).
   * Le `filter` courant (période/types) est PROPAGÉ en query params — le document ne contient QUE les
   * entrées de la période (DoD R3). Endpoint admin analytics `/admin/audit/export.json` (γ synchrone).
   */
  downloadComplianceAudit(filter: ComplianceAuditExportFilter): void;
}
